<script lang="ts">
  import { nextStep } from "../../lib/helpers/steps/menu";
  import { getCopy } from "../../lib/stores/copy";
  import Button from "../Button.svelte";
  import Carousel from "../Carousel.svelte";
  import { isAvailable, type MenuData, type MenuItem } from "../../lib/helpers/steps/menuItems";
  import VerticalMenuItemCard from "../Menu/VerticalMenuItemCard.svelte";
  import HorizontalMenuItemCard from "../Menu/HorizontalMenuItemCard.svelte";
  import ItemSelector from "../Menu/ItemSelector.svelte";
  import type { EmblaCarouselType } from "embla-carousel";

  export let menuData: MenuData;

  let isSelectorOpen = false;
  let selectedItem: MenuItem | undefined;
  let selectedVariation: string | undefined;
  let emblaApi: EmblaCarouselType;
  let continueButton: { scrollIntoView: () => void; };

  async function handleVariationConfirmed(event: CustomEvent<string>) {
    if (!selectedItem) return;

    selectedVariation = event.detail;
    isSelectorOpen = false;
    void (await nextStep(selectedItem, selectedVariation));
  }

  async function handleMenuItemClick(item: MenuItem, isDesktop = false) {
    selectedItem = item;
    if (emblaApi) {
      emblaApi.scrollTo(menuItems.indexOf(item));
    }
    if (isDesktop) {
      void (await mainButtonClick());
    } else {
      continueButton.scrollIntoView();
    }
  }

  function closeSelector() {
    isSelectorOpen = false;
  }

  async function mainButtonClick() {
    if (!selectedItem) return;
    if (!isAvailable(selectedItem)) return;

    if (selectedItem.variations?.length > 1) {
      isSelectorOpen = true;
    } else if (selectedItem.variations?.[0]?.isAvailable) {
      void (await nextStep(selectedItem, selectedItem.variations[0].name));
    }
  }

  $: isSingleOption = menuItems?.length === 1;
  $: {
    if (isSingleOption) {
      selectedItem = menuItems[0];
    }
  }
  $: copy = $getCopy("chooseItem") as Record<string, string>;
  $: buttonCopy = selectedItem?.variations?.length === 1 ? copy.orderButtonCopy : copy.selectOption;
  $: isDisabled = !selectedItem;
  $: menuItems = menuData.menuItems;
</script>

{#if isSelectorOpen}
  <ItemSelector on:close={closeSelector} item={selectedItem} on:confirm={handleVariationConfirmed} />
{/if}

<div class="w-full mx-auto text-center lg:text-left" class:singleOption={isSingleOption}>
  {#if menuItems}
    <div class="flex lg:hidden pt-4 content">
      {#if isSingleOption}
        <VerticalMenuItemCard item={menuItems[0]} on:click={() => handleMenuItemClick(menuItems[0])} active />
      {:else}
        <Carousel bind:emblaApi>
          {#each menuItems as item}
            <div class="embla__slide">
              <div class="flex justify-center h-full">
                <VerticalMenuItemCard
                  {item}
                  on:click={() => handleMenuItemClick(item)}
                  active={selectedItem === item}
                  unavailable={!isAvailable(item)} />
              </div>
            </div>
          {/each}
        </Carousel>
      {/if}
    </div>

    <div class="flex-col mt-8 gap-6 hidden lg:flex items-center">
      {#each menuItems as item}
        {#if isSingleOption}
          <VerticalMenuItemCard
            {item}
            on:click={() => handleMenuItemClick(item, true)}
            unavailable={!isAvailable(item)}
            active={selectedItem === item} />
        {:else}
          <HorizontalMenuItemCard
            {item}
            on:click={() => handleMenuItemClick(item, true)}
            unavailable={!isAvailable(item)}
            active={selectedItem === item} />
        {/if}
      {/each}
    </div>
  {/if}
  <div class="flex w-full justify-center lg:hidden mt-6 order-button lg:mt-12">
    <Button bind:this={continueButton} class="w-full lg:w-auto" primary on:click={mainButtonClick} disabled={isDisabled}>
      {buttonCopy}
    </Button>
  </div>
</div>

<style>
  .embla__slide {
    flex: 0 0 var(--slide-size);
    min-width: 0;
    padding-left: var(--slide-spacing);
  }

  .singleOption :global(.card.mobile) {
    max-width: 17rem;
  }

  .singleOption .content {
    width: 100%;
    justify-content: center;
  }

  .singleOption .content :global(button) {
    max-width: 17rem;
  }

  @media (min-width: 1024px) {
    .singleOption {
      justify-content: center;
      text-align: center;
      align-items: center;
    }

    .singleOption :global(.heading) {
      text-align: center;
    }

    .singleOption .order-button {
      display: flex;
    }
  }
</style>
